import React, { type MouseEvent, forwardRef } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import NewButton from '@atlaskit/button/new';
import { Flex, Text, Box } from '@atlaskit/primitives/compiled';
import { Show } from '@atlaskit/primitives/responsive';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import SparkleIcon from '@atlaskit/icon-lab/core/suggested-edit';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import AIContainer from '../ai-container/index.tsx';
import { AiIcon } from '../ai-icon/index.tsx';
import messages from './messages.tsx';

type Props = {
	autoFocus?: boolean;
	onClick: (_: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => void;
	onToggleBlur?: () => void;
};

const AIButtonOld = forwardRef<HTMLButtonElement, Props>(({ autoFocus, onClick }: Props, ref) => (
	<AIContainer spacing="none" isShadow>
		<Button
			ref={ref}
			autoFocus={autoFocus}
			appearance="subtle"
			onClick={onClick}
			spacing="none"
			// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
			aria-label="AI"
		>
			<Box paddingBlock="space.050" paddingInline="space.100">
				<Flex gap="space.050" alignItems="center" justifyContent="center">
					<AiIcon label="" size="small" />
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Text color="color.text.selected" weight="medium">
						AI
					</Text>
				</Flex>
			</Box>
		</Button>
	</AIContainer>
));

// Rendering the AI button as part of the NL2JQL CTA change
const renderAIButtonCta = () => {
	if (expValEquals('nl-to-jql-cta-change', 'variant', 'FIND_WORK', 'CONTROL')) {
		return <FormattedMessage {...messages.findWorkNonFinal} />;
	}

	if (expValEquals('nl-to-jql-cta-change', 'variant', 'QUICK_SEARCH', 'CONTROL')) {
		return <FormattedMessage {...messages.quickSearchNonFinal} />;
	}

	// This case is the ASK_AI variant
	return <FormattedMessage {...messages.askAiNonFinal} />;
};

const AIButtonNew = forwardRef<HTMLButtonElement, Props>(
	({ autoFocus, onClick, onToggleBlur }: Props, ref) => {
		const { formatMessage } = useIntl();

		return (
			<NewButton
				ref={ref}
				autoFocus={autoFocus}
				onClick={onClick}
				appearance="default"
				iconBefore={fg('nl-to-jql-cta-rovo2') ? SparkleIcon : AtlassianIntelligenceIcon}
				onBlur={onToggleBlur}
				testId="atlassian-intelligence.common.ui.ai-button.nl-2-jql-trigger-button"
			>
				{/* If we are above the `md` breakpoint, we render the full CTA text. Otherwise, we only render "AI" as the CTA text */}
				<Show above="md">{renderAIButtonCta()}</Show>
				<Show below="md">{formatMessage(messages.aiSearchShortNonFinal)}</Show>
			</NewButton>
		);
	},
);

export const AIButton = forwardRef<HTMLButtonElement, Props>(
	({ autoFocus, onClick, onToggleBlur }: Props, ref) => {
		if (
			!expValEquals('nl-to-jql-cta-change', 'variant', 'FIND_WORK', 'CONTROL') &&
			!expValEquals('nl-to-jql-cta-change', 'variant', 'QUICK_SEARCH', 'CONTROL') &&
			!expValEquals('nl-to-jql-cta-change', 'variant', 'ASK_AI', 'CONTROL')
		) {
			return <AIButtonOld autoFocus={autoFocus} onClick={onClick} ref={ref} />;
		}

		return (
			<AIButtonNew autoFocus={autoFocus} onClick={onClick} ref={ref} onToggleBlur={onToggleBlur} />
		);
	},
);
